import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, FileText, CalendarIcon, ListChecks, Sparkles } from "lucide-react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import RentalDetails from "@/components/RentalDetails";
import PaymentStatusBadge from "@/components/PaymentStatusBadge";

const STATUS_VALUES = ["agendado", "ativo", "concluido", "cancelado"];
const STATUS_CLS = {
  agendado: "bg-zinc-100 text-zinc-800 border-zinc-200",
  ativo: "bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/30",
  concluido: "bg-green-100 text-green-800 border-green-200",
  cancelado: "bg-red-100 text-red-800 border-red-200",
};

const empty = {
  car_id: "", customer_id: "",
  pickup_date: new Date(),
  return_date: new Date(Date.now() + 86400000),
  billing_period: "diaria",
  daily_rate: 0, weekly_rate: 0,
  deposit: 0, total: 0,
  status: "agendado", notes: ""
};

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

export default function Alugueis() {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cars, setCars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const [detailsId, setDetailsId] = useState(null);

  const load = async () => {
    const [r, c, k] = await Promise.all([api.get("/rentals"), api.get("/cars"), api.get("/customers")]);
    setItems(r.data); setCars(c.data); setCustomers(k.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => filter === "all" ? items : items.filter((i) => i.status === filter),
    [items, filter]
  );

  const days = useMemo(() => {
    if (!form.pickup_date || !form.return_date) return 0;
    return Math.max(1, differenceInCalendarDays(new Date(form.return_date), new Date(form.pickup_date)));
  }, [form.pickup_date, form.return_date]);

  const weeks = useMemo(() => Math.max(1, Math.ceil(days / 7)), [days]);

  useEffect(() => {
    if (form.billing_period === "semanal") {
      setForm((f) => ({ ...f, total: Number(f.weekly_rate || 0) * weeks }));
    } else {
      setForm((f) => ({ ...f, total: Number(f.daily_rate || 0) * days }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, weeks, form.daily_rate, form.weekly_rate, form.billing_period]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      car_id: it.car_id, customer_id: it.customer_id,
      pickup_date: parseISO(it.pickup_date), return_date: it.return_date ? parseISO(it.return_date) : new Date(Date.now() + 7 * 86400000),
      billing_period: it.billing_period || "diaria",
      daily_rate: it.daily_rate || 0, weekly_rate: it.weekly_rate || 0,
      deposit: it.deposit, total: it.total,
      status: it.status, notes: it.notes || ""
    });
    setOpen(true);
  };

  const onCarChange = (id) => {
    const car = cars.find((c) => c.id === id);
    const weekly = car ? Number(car.weekly_rate || (car.daily_price || 0) * 7) : 0;
    setForm((f) => ({
      ...f,
      car_id: id,
      daily_rate: car ? +(weekly / 7).toFixed(2) : f.daily_rate,
      weekly_rate: car ? weekly : f.weekly_rate,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        pickup_date: new Date(form.pickup_date).toISOString(),
        return_date: new Date(form.return_date).toISOString(),
        daily_rate: Number(form.daily_rate || 0),
        weekly_rate: Number(form.weekly_rate || 0),
        deposit: Number(form.deposit),
        total: Number(form.total),
      };
      if (editing) { await api.put(`/rentals/${editing.id}`, payload); toast.success(t("rentals.updated")); }
      else { await api.post("/rentals", payload); toast.success(t("rentals.created")); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("rentals.confirmDelete"))) return;
    try { await api.delete(`/rentals/${id}`); toast.success(t("rentals.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const periodLabel = form.billing_period === "semanal"
    ? `${weeks} ${weeks === 1 ? t("rentals.week") : t("rentals.weeks")} (${days} ${days === 1 ? t("rentals.day") : t("rentals.days")})`
    : `${days} ${days === 1 ? t("rentals.day") : t("rentals.days")}`;

  return (
    <div className="space-y-6" data-testid="alugueis-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">{t("rentals.pageTitle", { n: items.length })}</div>
          <p className="text-sm text-zinc-500 mt-1">{t("rentals.pageSubtitle")}</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="rounded-none w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {STATUS_VALUES.map((s) => <SelectItem key={s} value={s}>{t(`rentals.status.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            type="button"
            onClick={() => navigate("/aluguel/novo")}
            className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white"
            data-testid="wizard-launch-button"
          >
            <Sparkles className="w-4 h-4 mr-2" /> {t("rentals.wizardBtn")}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} variant="outline" className="rounded-none border-zinc-300" data-testid="add-rental-button">
                <Plus className="w-4 h-4 mr-2" /> {t("rentals.newRental")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? t("rentals.editRental") : t("rentals.newRental")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <Field label={t("rentals.vehicle")}>
                  <Select value={form.car_id} onValueChange={onCarChange}>
                    <SelectTrigger className="rounded-none" data-testid="rental-car-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                    <SelectContent>{cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.model} — {c.plate}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={t("rentals.customer")}>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="rounded-none" data-testid="rental-customer-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <DateField label={t("rentals.pickup")} date={form.pickup_date} onChange={(d) => setForm({ ...form, pickup_date: d })} dateLocale={dateLocale} />
                <DateField label={t("rentals.return")} date={form.return_date} onChange={(d) => setForm({ ...form, return_date: d })} dateLocale={dateLocale} />

                <Field label={t("rentals.billingType")} full>
                  <Select value={form.billing_period} onValueChange={(v) => setForm({ ...form, billing_period: v })}>
                    <SelectTrigger className="rounded-none" data-testid="rental-period-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diaria">{t("rentals.daily")}</SelectItem>
                      <SelectItem value="semanal">{t("rentals.weekly")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

                {form.billing_period === "diaria" ? (
                  <Field label={t("rentals.dailyRate")} full>
                    <Input type="number" step="0.01" className="rounded-none" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} data-testid="rental-daily-rate-input" />
                  </Field>
                ) : (
                  <Field label={t("rentals.weeklyRate")} full>
                    <Input type="number" step="0.01" className="rounded-none" value={form.weekly_rate} onChange={(e) => setForm({ ...form, weekly_rate: e.target.value })} data-testid="rental-weekly-rate-input" />
                  </Field>
                )}

                <Field label={t("rentals.deposit")}>
                  <Input type="number" step="0.01" className="rounded-none" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} />
                </Field>

                <Field label={t("common.status")}>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_VALUES.map((s) => <SelectItem key={s} value={s}>{t(`rentals.status.${s}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>

                <div className="col-span-2 border border-zinc-200 bg-zinc-50 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="overline">{t("rentals.rentalValue")}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {periodLabel} ×{" "}
                        {form.billing_period === "semanal"
                          ? fmtCurrency(form.weekly_rate) + t("rentals.perWeek")
                          : fmtCurrency(form.daily_rate) + t("rentals.perDay")}
                      </div>
                    </div>
                    <div className="font-display font-bold text-xl tracking-tighter" data-testid="rental-total-display">
                      {fmtCurrency(form.total)}
                    </div>
                  </div>
                  <div className="border-t border-zinc-300 pt-3 flex items-center justify-between">
                    <div>
                      <div className="overline text-[#F5A623]">{t("rentals.pickupTotal")}</div>
                      <div className="text-xs text-zinc-500 mt-1">{t("rentals.pickupHint")}</div>
                    </div>
                    <div className="font-display font-black text-3xl tracking-tighter text-[#F5A623]" data-testid="rental-pickup-total-display">
                      {fmtCurrency(Number(form.total || 0) + Number(form.deposit || 0))}
                    </div>
                  </div>
                </div>

                <Field label={t("common.notes")} full>
                  <Input className="rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </Field>

                <DialogFooter className="col-span-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                  <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-rental-button">{t("common.save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <FileText className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("rentals.emptyTitle")}</div>
          <p className="text-sm text-zinc-500">{t("rentals.emptySub")}</p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("rentals.vehicle")}</Th><Th>{t("rentals.customer")}</Th><Th>{t("rentals.pickup")}</Th><Th>{t("rentals.return")}</Th><Th>{t("rentals.billing")}</Th><Th>{t("common.total")}</Th><Th>{t("rentals.paymentCol")}</Th><Th>{t("common.status")}</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const periodTxt = (r.billing_period || "diaria") === "semanal"
                  ? `${fmtCurrency(r.weekly_rate)}${t("rentals.perWeek")}`
                  : `${fmtCurrency(r.daily_rate)}${t("rentals.perDay")}`;
                return (
                  <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`rental-row-${r.id}`}>
                    <Td className="font-medium">{r.car?.model || "—"} <span className="text-xs text-zinc-500 font-mono">({r.car?.plate})</span></Td>
                    <Td>{r.customer?.name || "—"}</Td>
                    <Td className="font-mono text-xs">{format(parseISO(r.pickup_date), "dd MMM yyyy", { locale: dateLocale })}</Td>
                    <Td className="font-mono text-xs">{r.return_date ? format(parseISO(r.return_date), "dd MMM yyyy", { locale: dateLocale }) : <span className="text-amber-600 italic">{t("rentals.openEnded")}</span>}</Td>
                    <Td className="font-mono text-xs">{periodTxt}</Td>
                    <Td className="font-mono font-semibold">{fmtCurrency(r.total)}</Td>
                    <Td>{r.status === "ativo" ? <PaymentStatusBadge paymentStatus={r.payment_status} size="sm" showAmount /> : <span className="text-zinc-400">—</span>}</Td>
                    <Td><span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${STATUS_CLS[r.status]}`}>{t(`rentals.status.${r.status}`)}</span></Td>
                    <Td>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setDetailsId(r.id)} title={t("rentals.detailsTitle")} className="p-1.5 border border-zinc-200 hover:border-[#F5A623] hover:text-[#F5A623]" data-testid={`rental-details-${r.id}`}><ListChecks className="w-3 h-3" /></button>
                        <button onClick={() => openEdit(r)} className="p-1.5 border border-zinc-200 hover:border-zinc-400"><Edit2 className="w-3 h-3" /></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <RentalDetails open={!!detailsId} rentalId={detailsId} onClose={() => setDetailsId(null)} onChange={load} />
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Field = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <label className="overline">{label}</label>{children}
  </div>
);

function DateField({ label, date, onChange, dateLocale }) {
  return (
    <div className="space-y-1.5">
      <label className="overline">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" type="button" className="rounded-none w-full justify-start font-normal">
            <CalendarIcon className="w-4 h-4 mr-2" />
            {date ? format(new Date(date), "dd MMM yyyy", { locale: dateLocale }) : "—"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-none">
          <Calendar mode="single" selected={new Date(date)} onSelect={onChange} locale={dateLocale} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
